import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { formatNumberWithoutCurrency } from '../../../../../../../utils/utils'
import { useEffect, useRef, useState } from 'react'
type SipEditIdealSipAmountInputProps = {
  inputId: string
  label: string
  value: number
  onChange: (value: number) => void
  onClear: () => void
  minValue?: number
  maxValue?: number
  testLocator?: string
}


export const SIP_EDIT_IDEAL_MAX_MONTHLY_AMOUNT = 1_00_00_000 // 1 crore

export function SipEditIdealSipAmountInput({
  inputId,
  label,
  value,
  onChange,
  onClear,
  minValue = 1,
  maxValue = SIP_EDIT_IDEAL_MAX_MONTHLY_AMOUNT,
  testLocator
}: SipEditIdealSipAmountInputProps) {
  const [clearedLocally, setClearedLocally] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (typeof value === 'number') setClearedLocally(false)
  }, [value])

  const displayValue = clearedLocally || value <= 0 ? '' : String(formatNumberWithoutCurrency(value))
  const isBelowMin = value > 0 && value < minValue
  const isAboveMax = value > maxValue
  const minError =
    minValue > 0 ? `Minimum amount is ${formatRupeeLabel({ rawAmount: minValue })}` : null
  const maxError =
    maxValue > 0 ? `Maximum amount is ${formatRupeeLabel({ rawAmount: maxValue })}` : null

  return (
    <div className="rounded-[12px] border border-[#EBF2F2] bg-white px-4 pt-3 pb-3 focus-within:border-[#232323]">
      <label
        htmlFor={inputId}
        className="mb-[2px] block text-[13px] font-[400] text-[#6A6E6E]"
        {...(testLocator ? { 'test-locator': testLocator } : {})}
      >
        {label}
      </label>
      <div className="flex items-baseline gap-1">
        <span className="shrink-0 text-[16px] font-[500] text-[#232323]" aria-hidden>
          ₹
        </span>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          ref={inputRef}
          placeholder=""
          value={displayValue}
          {...(testLocator ? { 'test-locator': `${testLocator}-input` } : {})}
          onChange={(event) => {
            const raw = event.target.value.replace(/\D/g, '')
            if (raw === '') {
              setClearedLocally(true)
              onClear()
              return
            }
            setClearedLocally(false)
            const num = parseInt(raw, 10)
            if (Number.isNaN(num)) return
            if (value > maxValue && num > value) return
            onChange(num)
          }}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-[16px] font-[500] text-[#232323] placeholder:text-[#6A6E6E] focus:outline-none focus:ring-0"
        />
      </div>
      {isBelowMin && minError ? (
        <p
          className="mt-2 text-[13px] font-[400] text-[#E77464]"
          {...(testLocator ? { 'test-locator': `${testLocator}-error-min` } : {})}
        >
          {minError}
        </p>
      ) : null}
      {isAboveMax && maxError ? (
        <p
          className="mt-2 text-[13px] font-[400] text-[#E77464]"
          {...(testLocator ? { 'test-locator': `${testLocator}-error-max` } : {})}
        >
          {maxError}
        </p>
      ) : null}
    </div>
  )
}
